import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PrivacyPolicyModal, TermsModal } from './LegalModals';
import { Instagram, Facebook, Youtube, MessageCircle, Send } from 'lucide-react';
import { getSiteConfig } from '../utils/siteConfig';
import { useLanguage } from '../utils/i18n';
export function Footer() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const config = getSiteConfig();
  const social = config.social;
  const {
    t
  } = useLanguage();
  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 400);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const socialLinks = [{
    href: social.instagram,
    icon: <Instagram className="h-4 w-4" data-id="element-157" />,
    label: 'Instagram'
  }, {
    href: social.whatsapp,
    icon: <MessageCircle className="h-4 w-4" data-id="element-158" />,
    label: 'WhatsApp'
  }, {
    href: social.telegram,
    icon: <Send className="h-4 w-4" data-id="element-159" />,
    label: 'Telegram'
  }, {
    href: social.facebook,
    icon: <Facebook className="h-4 w-4" data-id="element-160" />,
    label: 'Facebook'
  }, {
    href: social.youtube,
    icon: <Youtube className="h-4 w-4" data-id="element-161" />,
    label: 'YouTube'
  }].filter(s => s.href);
  return <footer className="bg-gray-900 text-white pt-16 pb-8 border-t border-gray-800" data-id="element-162">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-163">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-164">
          {/* Brand */}
          <div data-id="element-165">
            <div className="flex items-center gap-3 mb-6" data-id="element-166">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hyseuuq2warchstz2tvf3s/f7c74e3b-3f4e-439c-8223-13536fb8e81c/YB_A2.svg" alt="Yavuz Burak Logo" className="h-14 w-auto object-contain" data-id="element-167" />
              
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-168">
              {t('footer_desc')}
            </p>

            {/* Social Icons */}
            {socialLinks.length > 0 && <div className="flex items-center gap-2 flex-wrap" data-id="element-169">
                {socialLinks.map(s => <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-9 h-9 bg-white/5 hover:bg-red-600 border border-white/10 hover:border-red-600 rounded-sm flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200" data-id="element-170">
                
                    {s.icon}
                  </a>)}
              </div>}
          </div>

          {/* Navigation */}
          <div data-id="element-171">
            <h4 className="font-heading font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-172">
              {t('footer_nav')}
            </h4>
            <ul className="space-y-3 text-gray-400 text-sm" data-id="element-173">
              <li data-id="element-174">
                <Link to="/catalog" className="hover:text-red-500 transition-colors" data-id="element-175">
                  
                  {t('footer_catalog_link')}
                </Link>
              </li>
              <li data-id="element-176">
                <button onClick={() => scrollToSection('applications')} className="hover:text-red-500 transition-colors text-left" data-id="element-177">
                  
                  {t('footer_areas_link')}
                </button>
              </li>
              <li data-id="element-178">
                <button onClick={() => scrollToSection('about')} className="hover:text-red-500 transition-colors text-left" data-id="element-179">
                  
                  {t('footer_about_link')}
                </button>
              </li>
              <li data-id="element-180">
                <Link to="/contact" className="hover:text-red-500 transition-colors" data-id="element-181">
                  
                  {t('footer_contact_link')}
                </Link>
              </li>
              <li data-id="element-182">
                <Link to="/partner" className="hover:text-red-500 transition-colors" data-id="element-183">
                  
                  {t('footer_partner_link')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div data-id="element-184">
            <h4 className="font-heading font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-185">
              {t('footer_products')}
            </h4>
            <ul className="space-y-3 text-gray-400 text-sm" data-id="element-186">
              <li data-id="element-187">
                <Link to="/catalog#exterra" className="hover:text-red-500 transition-colors" data-id="element-188">
                  
                  {t('footer_facade')}
                </Link>
              </li>
              <li data-id="element-189">
                <Link to="/catalog#velora" className="hover:text-red-500 transition-colors" data-id="element-190">
                  
                  {t('footer_interior')}
                </Link>
              </li>
              <li data-id="element-191">
                <Link to="/catalog#primer" className="hover:text-red-500 transition-colors" data-id="element-192">
                  
                  {t('footer_primers')}
                </Link>
              </li>
              <li data-id="element-193">
                <Link to="/catalog#betono" className="hover:text-red-500 transition-colors" data-id="element-194">
                  
                  {t('footer_betono')}
                </Link>
              </li>
              <li data-id="element-195">
                <Link to="/catalog#microlux" className="hover:text-red-500 transition-colors" data-id="element-196">
                  
                  {t('footer_micro')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div data-id="element-197">
            <h4 className="font-heading font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-198">
              {t('contact_label')}
            </h4>
            <ul className="space-y-3 text-gray-400 text-sm" data-id="element-199">
              <li data-id="element-200">{config.contacts.address}</li>
              <li data-id="element-201">
                <a href={`tel:${config.contacts.phone.replace(/\s/g, '')}`} className="hover:text-red-500 transition-colors" data-id="element-202">
                  
                  {config.contacts.phone}
                </a>
              </li>
              <li data-id="element-203">
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors" data-id="element-204">
                  
                  {config.contacts.instagram}
                </a>
              </li>
              <li className="pt-2" data-id="element-205">
                <button onClick={() => scrollToSection('contact')} className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium uppercase tracking-wide px-4 py-2 rounded-sm transition-colors" data-id="element-206">
                  
                  {t('footer_write_us')}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500" data-id="element-207">
          <p data-id="element-208">
            {t('footer_rights', {
            year: new Date().getFullYear().toString()
          })}
          </p>
          <div className="flex gap-6" data-id="element-209">
            <button onClick={() => setIsPrivacyOpen(true)} className="hover:text-white transition-colors" data-id="element-210">
              
              {t('footer_privacy')}
            </button>
            <button onClick={() => setIsTermsOpen(true)} className="hover:text-white transition-colors" data-id="element-211">
              
              {t('footer_terms')}
            </button>
          </div>
        </div>
      </div>

      <PrivacyPolicyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} data-id="element-212" />
      
      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} data-id="element-213" />
    </footer>;
}